///
/// EarthBrowser
///
/// Created by Matt Giger on 10/31/2026
/// Copyright (c) 2026 Geodata Labs
///
/// One instanced draw for every webcam cluster badge on screen: a
/// fixed-pixel disc with a ring and the member count (webcam_cluster_badge
/// .glsl). Same frame protocol as EmberDomeBatch: the cloud node appends
/// badges while it is traversed and the array is uploaded when this node's
/// draw executes, so nothing is allocated per badge or per frame.
///
/// The count is set from a small digit strip rasterized once with Canvas
/// 2D; each instance carries its glyph indices packed four bits apiece.
///

import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet, Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type color } from "@stovokor/math/color"
import { cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { type time } from "@stovokor/math/time"
import { vec2 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

// center.xyz, color rgba, radiusPx / opacity / glyphs / hover, offset px
const cFloatsPerInstance = 13
const cInitialCapacity = 256
const cBoundsKm = 1e7

// Glyph strip: digits then 'k'. Cells are 0.6 em wide and 1.5 em tall so
// the shader can lay the count out with a constant advance
const cGlyphs = "0123456789k"
const cAtlasEm = 40
const cAtlasCellWidth = Math.round(cAtlasEm * 0.6)
const cAtlasCellHeight = Math.round(cAtlasEm * 1.5)
const cMaxGlyphs = 4

/** Pack a count into the shader's glyph word: up to four 4-bit glyph indices plus the glyph count in bits 16-18 */
export function packCount(count: number): number {
	let text: string
	if (count < 1000) {
		text = String(count)
	} else {
		// Nearest thousand; the hover card carries the exact number
		text = `${Math.round(count / 1000)}k`
	}
	if (text.length > cMaxGlyphs) text = "9k"
	let packed = text.length << 16
	for (let i = 0; i < text.length; i++) {
		const g = cGlyphs.indexOf(text[i])
		packed |= (g < 0 ? 0 : g) << (i * 4)
	}
	return packed
}

function rasterizeGlyphs(): HTMLCanvasElement | undefined {
	if (typeof document === "undefined") return undefined
	const canvas = document.createElement("canvas")
	canvas.width = cAtlasCellWidth * cGlyphs.length
	canvas.height = cAtlasCellHeight
	const ctx = canvas.getContext("2d")
	if (!ctx) return undefined
	ctx.clearRect(0, 0, canvas.width, canvas.height)
	ctx.fillStyle = "#ffffff"
	ctx.font = `600 ${cAtlasEm}px -apple-system, system-ui, "Segoe UI", Helvetica, Arial, sans-serif`
	ctx.textAlign = "center"
	ctx.textBaseline = "middle"
	for (let i = 0; i < cGlyphs.length; i++) {
		ctx.fillText(cGlyphs[i], (i + 0.5) * cAtlasCellWidth, cAtlasCellHeight * 0.5)
	}
	return canvas
}

class WebcamBadgeGeometry implements Geometry {
	public corner: VertexAttribute
	public instanceCenter: VertexAttribute
	public instanceColor: VertexAttribute
	public instanceParams: VertexAttribute
	public instanceOffset: VertexAttribute
	public vertexBuffer: VertexBuffer
	public instanceBuffer: VertexBuffer
	public indexBuffer: IndexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds = new cube(-cBoundsKm, cBoundsKm)
	public boundingSphere = new sphere(cBoundsKm)
	public available: boolean = true
	public instanceCount: number = 0

	constructor(context: StovokorContext) {
		this.corner = new VertexAttribute(context, 2, [-1, -1, 1, -1, 1, 1, -1, 1])
		this.vertexBuffer = new VertexBuffer(context, [this.corner])
		this.indexBuffer = new IndexBuffer(context)
		this.indexBuffer.quad(0, 1, 2, 3)

		this.instanceCenter = new VertexAttribute(context, 3)
		this.instanceColor = new VertexAttribute(context, 4)
		this.instanceParams = new VertexAttribute(context, 4)
		this.instanceOffset = new VertexAttribute(context, 2)
		const instanced = [this.instanceCenter, this.instanceColor, this.instanceParams, this.instanceOffset]
		for (const attribute of instanced) {
			attribute.divisor = 1
		}
		this.instanceBuffer = new VertexBuffer(context, instanced)
		this.instanceBuffer.usage = WebGL2RenderingContext.DYNAMIC_DRAW
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.instanceBuffer.destroy()
		this.indexBuffer.destroy()
	}
}

class WebcamBadgeRenderer extends Renderer {
	public modelView = new UniformDynamic<matrix>()
	public projection = new UniformDynamic<matrix>()
	public viewport = new UniformDynamic<vec2>(new vec2(1, 1))
	public glyphs = new UniformDynamic<Texture>()

	constructor(context: StovokorContext, geometry: Geometry, glyphs: Texture, finalize: () => void) {
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("webcam_cluster_badge"), geometry, [transparentMaterial])
		this.glyphs.value = glyphs
		this.drawState.cullFace = false
		this.preActions = [finalize]
	}

	protected stateUpdate(state: RenderState): void {
		this.modelView.value = state.modelView
		this.projection.value = state.projection
		this.viewport.value = new vec2(state.width, state.height)
	}
}

export class WebcamBadgeBatch extends NodeBase {
	private readonly geometry: WebcamBadgeGeometry
	private readonly glyphTexture: Texture
	private data = new Float32Array(cFloatsPerInstance * cInitialCapacity)
	private count = 0
	private frame?: time

	constructor(context: StovokorContext) {
		super()
		// Badges draw after the chips and domes of the same pass so a count
		// placed on a chip's corner sits on top of it
		this.renderOrder = 1
		this.geometry = new WebcamBadgeGeometry(context)
		this.glyphTexture = new Texture(context)
		const canvas = rasterizeGlyphs()
		if (canvas) {
			this.glyphTexture.uploadCanvas(canvas, canvas.width, canvas.height, false)
		}
		this.renderer = new WebcamBadgeRenderer(context, this.geometry, this.glyphTexture, () => this.finalize())
	}

	public override destroy(): void {
		super.destroy()
		this.glyphTexture.destroy()
	}

	/** Badges appended for the frame being drawn (debugging) */
	public get instanceCount(): number {
		return this.count
	}

	/**
	 * Append one badge at world point (x, y, z) km: `radiusPx` disc radius
	 * in logical pixels, `packed` from packCount(), `hover` 0..1, and an
	 * optional screen offset in px (y down) from the anchor, e.g. to sit the
	 * count on the corner of a thumbnail chip
	 */
	public add(
		state: RenderState, x: number, y: number, z: number, radiusPx: number, ring: color, opacity: number,
		packed: number, hover: number, offsetX: number = 0, offsetY: number = 0
	): void {
		this.beginFrame(state)
		if ((this.count + 1) * cFloatsPerInstance > this.data.length) {
			const grown = new Float32Array(this.data.length * 2)
			grown.set(this.data)
			this.data = grown
		}
		const d = this.data
		let i = this.count * cFloatsPerInstance
		d[i++] = x
		d[i++] = y
		d[i++] = z
		d[i++] = ring.r
		d[i++] = ring.g
		d[i++] = ring.b
		d[i++] = ring.a
		d[i++] = radiusPx
		d[i++] = opacity
		d[i++] = packed
		d[i++] = hover
		d[i++] = offsetX
		d[i] = offsetY
		this.count += 1
	}

	public override render(state: RenderState): RenderActionSet[] {
		this.beginFrame(state)
		return super.render(state)
	}

	private beginFrame(state: RenderState): void {
		if (this.frame !== state.realTime) {
			this.frame = state.realTime
			this.count = 0
		}
	}

	private finalize(): void {
		this.geometry.instanceCount = this.count
		if (this.count > 0) {
			// Whole capacity each time so the buffer takes a bufferSubData
			// rather than a reallocation; instanceCount limits the draw
			this.geometry.instanceBuffer.uploadData(this.data)
		}
	}
}

// One batch per GL context
const sharedBatches = new WeakMap<StovokorContext, WebcamBadgeBatch>()

export function sharedWebcamBadgeBatch(context: StovokorContext): WebcamBadgeBatch {
	let batch = sharedBatches.get(context)
	if (!batch) {
		batch = new WebcamBadgeBatch(context)
		sharedBatches.set(context, batch)
	}
	return batch
}
